/**
 * Cash Flow Recovery Planner
 * ==========================
 * Purpose: help the owner decide how big a bank loan to take out to fully
 * escape a supplier-debt / cash-flow crunch — not just kick the can down
 * the road.
 *
 * Model per month:
 *   beginning       = ending of previous month (or user's current cash)
 *   inflows         = expected sales revenue
 *   outflows        = fixed opex + monthly supplier debt payment
 *                     + monthly loan repayment (P&I) + monthly restock spend
 *   ending          = beginning + inflows − outflows
 *
 * "Loan needed" is calculated three ways and displayed side-by-side so the
 * owner can pick their risk appetite (option a / b / c per the intake).
 *
 * All numbers are in ZAR. Everything is client-side except for the initial
 * `/cash-flow/snapshot` GET that seeds sensible defaults.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Wallet,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Info,
  Landmark,
  RefreshCw,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const num = (v, fb = 0) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : fb;
};

const fmt = (n) => {
  const v = num(n);
  return `R ${v.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Monthly amortising loan repayment (P&I) — standard annuity formula. */
const monthlyLoanRepayment = (principal, annualRatePct, months) => {
  const P = num(principal);
  const r = num(annualRatePct) / 100 / 12;
  const n = Math.max(1, Math.round(num(months)));
  if (P <= 0) return 0;
  if (r === 0) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
};

export default function CashFlowPlanner() {
  // Snapshot from backend — auto-loaded defaults.
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);

  // User-editable inputs.
  const [currentCash, setCurrentCash] = useState(0);
  const [supplierDebt, setSupplierDebt] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [monthlyOpex, setMonthlyOpex] = useState(0);
  const [monthlyRestock, setMonthlyRestock] = useState(0);
  const [projectionMonths, setProjectionMonths] = useState(6);
  const [supplierPayoffMonths, setSupplierPayoffMonths] = useState(6);

  // Loan modelling.
  const [loanAnnualRate, setLoanAnnualRate] = useState(12); // typical SA business overdraft
  const [loanTermMonths, setLoanTermMonths] = useState(24);
  const [chosenLoanAmount, setChosenLoanAmount] = useState(0);
  // Working-capital cushion: how many DAYS of operating spend must be
  // sitting in the till at any given moment. This is the intra-month
  // timing gap — revenue drips in daily but stock has to be bought in
  // lumps up-front, so on the 15th you can be broke even if the
  // monthly math is positive. Default 21 days ≈ typical hardware
  // stock-turn + customer account terms. Tuneable.
  const [workingCapitalDays, setWorkingCapitalDays] = useState(21);

  const loadSnapshot = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/cash-flow/snapshot`);
      setSnapshot(res.data);
      setSupplierDebt(res.data.supplier_debt_total);
      setMonthlyRevenue(res.data.avg_monthly_revenue);
      setMonthlyOpex(res.data.avg_monthly_opex);
      setMonthlyRestock(res.data.avg_monthly_supplier_restock);
    } catch (e) {
      toast.error(`Failed to load cash-flow snapshot: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, []);

  // ================================================================
  // PROJECTION — same math run twice: once WITHOUT loan, once WITH the
  // owner's chosen loan amount, so we can compare the two on-screen.
  // ================================================================
  const buildProjection = (loanAmount) => {
    const rows = [];
    const monthlyLoanPmt = monthlyLoanRepayment(
      loanAmount,
      loanAnnualRate,
      loanTermMonths,
    );
    // The supplier-debt payoff is spread over `supplierPayoffMonths`.
    // If a loan is taken, we assume the loan proceeds go straight to
    // clearing supplier debt on day 1 — so the monthly supplier
    // payoff cost drops accordingly.
    const remainingDebtAfterLoan = Math.max(0, num(supplierDebt) - num(loanAmount));
    const supplierMonthlyPay = remainingDebtAfterLoan / Math.max(1, num(supplierPayoffMonths));

    let cash = num(currentCash) + num(loanAmount); // loan proceeds arrive day 1
    for (let m = 1; m <= projectionMonths; m++) {
      const begin = cash;
      const inflows = num(monthlyRevenue);
      const opex = num(monthlyOpex);
      const restock = num(monthlyRestock);
      // Supplier debt payoff only runs for the payoff window; after
      // that, month payment drops to zero.
      const supplierPay = m <= supplierPayoffMonths ? supplierMonthlyPay : 0;
      // Loan repayment runs for the loan term.
      const loanPay = m <= loanTermMonths ? monthlyLoanPmt : 0;
      const outflows = opex + restock + supplierPay + loanPay;
      const net = inflows - outflows;
      const end = begin + net;
      cash = end;
      rows.push({
        month: m,
        begin,
        inflows,
        opex,
        restock,
        supplierPay,
        loanPay,
        outflows,
        net,
        end,
      });
    }
    return {
      rows,
      lowestEnding: rows.reduce(
        (min, r) => (r.end < min ? r.end : min),
        rows[0]?.end ?? 0,
      ),
      finalEnding: rows[rows.length - 1]?.end ?? 0,
      monthlyLoanPmt,
    };
  };

  // No-loan projection to derive the worst-case cash gap.
  const noLoan = useMemo(
    () => buildProjection(0),
    [currentCash, supplierDebt, monthlyRevenue, monthlyOpex, monthlyRestock, projectionMonths, supplierPayoffMonths, loanAnnualRate, loanTermMonths],
  );

  // Three loan-sizing suggestions.
  //   • Bare Minimum   = just cover the worst-month cash gap (0 if the
  //                       projection is already cash-positive).
  //   • Recommended    = worst gap + working-capital cushion + 1 month
  //                       fixed opex, LESS the cash you already have.
  //                       This is what you'd typically ask a bank for:
  //                       enough to smooth out the intra-month lumps
  //                       and stay solvent through a bad week.
  //   • Clear It All   = pay every supplier off in one shot + working-
  //                       capital cushion + 1 month opex, LESS cash on
  //                       hand. Kills supplier pressure entirely AND
  //                       leaves enough in the tank to keep buying
  //                       stock without waiting for customer cash.
  const suggestions = useMemo(() => {
    const worstGap = Math.max(0, -noLoan.lowestEnding);
    const safetyBuffer = num(monthlyOpex);
    const fullDebt = num(supplierDebt);
    const cash = num(currentCash);
    // Daily operating cash outflow (opex + restock is the pool you
    // draw from during the month to keep the doors open + shelves full).
    const dailySpend = (num(monthlyOpex) + num(monthlyRestock)) / 30;
    const workingCapital = dailySpend * num(workingCapitalDays);
    const roundUp1k = (v) => Math.ceil(Math.max(0, v) / 1000) * 1000;
    return {
      minimal: roundUp1k(worstGap),
      buffered: roundUp1k(worstGap + workingCapital + safetyBuffer - cash),
      clearDebt: roundUp1k(fullDebt + workingCapital + safetyBuffer - cash),
      workingCapital, // exposed for the on-screen explainer
      dailySpend,
    };
  }, [noLoan, monthlyOpex, monthlyRestock, supplierDebt, currentCash, workingCapitalDays]);

  const withLoan = useMemo(
    () => buildProjection(num(chosenLoanAmount)),
    [chosenLoanAmount, currentCash, supplierDebt, monthlyRevenue, monthlyOpex, monthlyRestock, projectionMonths, supplierPayoffMonths, loanAnnualRate, loanTermMonths],
  );

  // Cash runway (no-loan scenario) — how many months until we run out.
  const runway = useMemo(() => {
    const burn = num(monthlyOpex) + num(monthlyRestock) + num(supplierDebt) / Math.max(1, num(supplierPayoffMonths)) - num(monthlyRevenue);
    if (burn <= 0) return { months: Infinity, healthy: true };
    return { months: num(currentCash) / burn, healthy: false };
  }, [currentCash, monthlyOpex, monthlyRestock, monthlyRevenue, supplierDebt, supplierPayoffMonths]);

  const runwayLabel = runway.months === Infinity
    ? "Positive cash flow — no runway needed"
    : `${runway.months.toFixed(1)} months`;
  const runwayColor = runway.healthy
    ? "text-emerald-700"
    : runway.months < 3
      ? "text-rose-700"
      : "text-amber-700";

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="cash-flow-planner">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Landmark className="h-6 w-6 text-indigo-700" />
            Cash Flow Recovery Planner
          </h1>
          <p className="text-sm text-slate-600">
            Work out exactly how much to borrow so you clear supplier debt
            <em> and </em> keep positive cash. Live supplier balances auto-
            loaded from your books.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          onClick={loadSnapshot}
          disabled={loading}
          data-testid="reload-snapshot-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh from books
        </Button>
        <Button
          onClick={async () => {
            // Downloads the Bank Loan Overview PDF. Uses current-cash so
            // the "Cash on hand" line matches the on-screen number.
            try {
              const res = await axios.get(`${API}/api/cash-flow/bank-report`, {
                params: { months: 4, current_cash: num(currentCash) },
                responseType: "blob",
              });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const link = document.createElement("a");
              link.href = url;
              link.download = `bank-loan-overview-${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              toast.success("Bank Loan Overview PDF downloaded");
            } catch (e) {
              toast.error(`Failed to download report: ${e.message}`);
            }
          }}
          className="bg-indigo-700 hover:bg-indigo-800"
          data-testid="download-bank-report-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Download for bank
        </Button>
        </div>
      </div>

      {/* ============================ INPUTS ============================ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="h-4 w-4 text-indigo-600" />
              Where you stand today
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="cf-cash">Current cash on hand (R)</Label>
              <Input
                id="cf-cash"
                type="number"
                step="100"
                value={currentCash}
                onChange={(e) => setCurrentCash(e.target.value)}
                data-testid="cf-current-cash"
                placeholder="Bank + till float + petty cash"
              />
              <p className="text-xs text-slate-500 mt-1">
                Only YOU know this — we don&apos;t auto-pull banking data.
              </p>
            </div>
            <div>
              <Label htmlFor="cf-debt">Total supplier debt (R)</Label>
              <Input
                id="cf-debt"
                type="number"
                step="100"
                value={supplierDebt}
                onChange={(e) => setSupplierDebt(e.target.value)}
                data-testid="cf-supplier-debt"
              />
              <p className="text-xs text-slate-500 mt-1">
                Auto-loaded from Suppliers module ({snapshot?.supplier_debt_breakdown?.length || 0} suppliers).
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Monthly cash movements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="cf-rev">Expected monthly revenue (R)</Label>
              <Input
                id="cf-rev"
                type="number"
                step="1000"
                value={monthlyRevenue}
                onChange={(e) => setMonthlyRevenue(e.target.value)}
                data-testid="cf-revenue"
              />
              <p className="text-xs text-slate-500 mt-1">Default: avg last 3 months of sales.</p>
            </div>
            <div>
              <Label htmlFor="cf-opex">Fixed monthly operating expenses (R)</Label>
              <Input
                id="cf-opex"
                type="number"
                step="500"
                value={monthlyOpex}
                onChange={(e) => setMonthlyOpex(e.target.value)}
                data-testid="cf-opex"
              />
              <p className="text-xs text-slate-500 mt-1">Rent, salaries, utilities — from Income Statement.</p>
            </div>
            <div>
              <Label htmlFor="cf-restock">Monthly restock spend (R)</Label>
              <Input
                id="cf-restock"
                type="number"
                step="500"
                value={monthlyRestock}
                onChange={(e) => setMonthlyRestock(e.target.value)}
                data-testid="cf-restock"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ongoing stock purchases (new supplier debt). Default: avg last 3 months of GRVs.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Landmark className="h-4 w-4 text-purple-600" />
              Loan &amp; payoff plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>
                Projection window: <strong>{projectionMonths} months</strong>
              </Label>
              <Slider
                min={3}
                max={24}
                step={1}
                value={[projectionMonths]}
                onValueChange={([v]) => setProjectionMonths(v)}
                className="mt-2"
                data-testid="cf-projection-slider"
              />
            </div>
            <div>
              <Label>
                Pay off supplier debt over: <strong>{supplierPayoffMonths} months</strong>
              </Label>
              <Slider
                min={1}
                max={24}
                step={1}
                value={[supplierPayoffMonths]}
                onValueChange={([v]) => setSupplierPayoffMonths(v)}
                className="mt-2"
                data-testid="cf-payoff-slider"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cf-rate">Loan interest % p.a.</Label>
                <Input
                  id="cf-rate"
                  type="number"
                  step="0.5"
                  value={loanAnnualRate}
                  onChange={(e) => setLoanAnnualRate(e.target.value)}
                  data-testid="cf-loan-rate"
                />
              </div>
              <div>
                <Label htmlFor="cf-term">Loan term (months)</Label>
                <Input
                  id="cf-term"
                  type="number"
                  step="1"
                  value={loanTermMonths}
                  onChange={(e) => setLoanTermMonths(e.target.value)}
                  data-testid="cf-loan-term"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cf-wc-days">
                Intra-month working capital: <strong>{workingCapitalDays} days</strong>
              </Label>
              <Slider
                id="cf-wc-days"
                min={0}
                max={60}
                step={1}
                value={[workingCapitalDays]}
                onValueChange={([v]) => setWorkingCapitalDays(v)}
                className="mt-2"
                data-testid="cf-wc-days-slider"
              />
              <p className="text-xs text-slate-600 mt-1">
                How many days of stock spend + opex you need in the till
                at any moment to smoothly restock while waiting for
                customer cash. Hardware retail typical: 21–30 days.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============================ VERDICT ============================ */}
      <Card data-testid="cf-recommendation">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">How much should you borrow?</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Intra-month cash-flow explainer — makes the "working
              capital" component of each loan option visible so the
              owner understands WHY it's in the recommendation. */}
          <Alert className="mb-4 border-slate-200 bg-slate-50">
            <Info className="h-4 w-4 text-slate-600" />
            <AlertDescription className="text-xs text-slate-800 space-y-0.5">
              <div>
                Daily operating outflow (opex + restock ÷ 30):{" "}
                <strong>{fmt(suggestions.dailySpend)}/day</strong>
              </div>
              <div>
                Working capital needed for {workingCapitalDays} days of
                stock &amp; running costs:{" "}
                <strong>{fmt(suggestions.workingCapital)}</strong>
              </div>
              <div className="text-slate-600">
                This is the cash you must keep in the till so you can
                keep buying stock while customer payments trickle in —
                the answer to &quot;why do I run out of cash mid-month
                even though the P&amp;L looks fine?&quot;.
              </div>
            </AlertDescription>
          </Alert>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              {
                key: "minimal",
                title: "Bare minimum",
                subtitle: suggestions.minimal === 0
                  ? "You're projected cash-positive — no loan needed to stay solvent"
                  : "Just cover the worst-month cash gap",
                amount: suggestions.minimal,
                accent: "border-slate-300 bg-slate-50",
                testid: "loan-minimal",
              },
              {
                key: "buffered",
                title: "Recommended",
                subtitle: `Worst gap + ${workingCapitalDays}-day working capital + 1 month opex`,
                amount: suggestions.buffered,
                accent: "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200",
                testid: "loan-buffered",
              },
              {
                key: "clearDebt",
                title: "Clear it all",
                subtitle: `Pay every supplier + ${workingCapitalDays}-day working capital + 1 month opex`,
                amount: suggestions.clearDebt,
                accent: "border-indigo-400 bg-indigo-50",
                testid: "loan-cleardebt",
              },
            ].map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => setChosenLoanAmount(opt.amount)}
                className={`text-left rounded-lg border-2 p-4 transition ${opt.accent} hover:shadow-md`}
                data-testid={opt.testid}
              >
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {opt.title}
                </div>
                <div className="text-2xl font-bold text-slate-900 mt-1 font-mono">
                  {fmt(opt.amount)}
                </div>
                <div className="text-xs text-slate-600 mt-1">{opt.subtitle}</div>
                <div className="text-xs text-slate-500 mt-2">
                  Monthly repayment:{" "}
                  <strong>{fmt(monthlyLoanRepayment(opt.amount, loanAnnualRate, loanTermMonths))}</strong>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-end">
            <div className="flex-1">
              <Label htmlFor="cf-chosen">Model a custom loan amount (R)</Label>
              <Input
                id="cf-chosen"
                type="number"
                step="1000"
                value={chosenLoanAmount}
                onChange={(e) => setChosenLoanAmount(e.target.value)}
                data-testid="cf-chosen-loan"
              />
            </div>
            <div className="text-sm text-slate-700">
              Repayment on <strong>{fmt(chosenLoanAmount)}</strong> @{" "}
              {num(loanAnnualRate)}% over {num(loanTermMonths)} months ={" "}
              <strong>{fmt(withLoan.monthlyLoanPmt)}</strong>/month
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============================ RUNWAY ============================ */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            {runway.healthy ? (
              <TrendingUp className="h-6 w-6 text-emerald-600 shrink-0" />
            ) : (
              <TrendingDown className={`h-6 w-6 shrink-0 ${runwayColor}`} />
            )}
            <div>
              <div className="text-sm text-slate-600">Cash runway (no-loan scenario)</div>
              <div className={`text-2xl font-bold ${runwayColor}`} data-testid="cf-runway">
                {runwayLabel}
              </div>
              {!runway.healthy && runway.months < 3 && (
                <Alert className="mt-3 border-rose-300 bg-rose-50">
                  <AlertTriangle className="h-4 w-4 text-rose-700" />
                  <AlertDescription className="text-sm text-rose-800">
                    <strong>Critical.</strong> At current burn rate you&apos;ll
                    hit zero cash in under 3 months without a loan or a
                    revenue lift.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============================ PROJECTION TABLE ============================ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {num(chosenLoanAmount) > 0
              ? `Projection WITH loan of ${fmt(chosenLoanAmount)}`
              : "Projection WITHOUT loan"}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Begin</TableHead>
                <TableHead className="text-right">Inflows</TableHead>
                <TableHead className="text-right">Opex</TableHead>
                <TableHead className="text-right">Restock</TableHead>
                <TableHead className="text-right">Supplier pay-down</TableHead>
                <TableHead className="text-right">Loan repay</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">Ending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withLoan.rows.map((r) => (
                <TableRow key={r.month} data-testid={`cf-row-month-${r.month}`}>
                  <TableCell>M{r.month}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.begin)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{fmt(r.inflows)}</TableCell>
                  <TableCell className="text-right font-mono text-rose-700">−{fmt(r.opex)}</TableCell>
                  <TableCell className="text-right font-mono text-rose-700">−{fmt(r.restock)}</TableCell>
                  <TableCell className="text-right font-mono text-rose-700">−{fmt(r.supplierPay)}</TableCell>
                  <TableCell className="text-right font-mono text-rose-700">−{fmt(r.loanPay)}</TableCell>
                  <TableCell className={`text-right font-mono ${r.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {r.net >= 0 ? "+" : ""}{fmt(r.net)}
                  </TableCell>
                  <TableCell className={`text-right font-mono font-bold ${r.end >= 0 ? "text-emerald-800" : "text-rose-700"}`}>
                    {fmt(r.end)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-3 text-xs text-slate-600">
            Worst month ending balance:{" "}
            <strong className={withLoan.lowestEnding < 0 ? "text-rose-700" : "text-emerald-700"}>
              {fmt(withLoan.lowestEnding)}
            </strong>
            {" · "}
            Final month ending balance:{" "}
            <strong className={withLoan.finalEnding >= 0 ? "text-emerald-700" : "text-rose-700"}>
              {fmt(withLoan.finalEnding)}
            </strong>
          </div>
        </CardContent>
      </Card>

      {/* ============================ SUPPLIER BREAKDOWN ============================ */}
      {snapshot?.supplier_debt_breakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Who you owe (top 10)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">
                    Monthly pay-off over {supplierPayoffMonths}mo
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshot.supplier_debt_breakdown.slice(0, 10).map((s) => (
                  <TableRow key={s.supplier_id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(s.balance)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-600">
                      {fmt(s.balance / Math.max(1, supplierPayoffMonths))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ============================ TIPS ============================ */}
      <Alert className="border-indigo-200 bg-indigo-50">
        <Info className="h-4 w-4 text-indigo-700" />
        <AlertDescription className="text-sm text-indigo-900 space-y-1">
          <p><strong>Optimise your free cash flow:</strong></p>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            <li>Accelerate inflows — offer 2% early-settlement discounts to account customers who pay within 7 days.</li>
            <li>Delay outflows — negotiate 60-day terms with your two biggest suppliers before you take the loan.</li>
            <li>If the &quot;Recommended&quot; loan still leaves any month in the red, either extend the loan term OR extend the supplier payoff window.</li>
            <li>If the final ending balance is negative, monthly revenue is too low to service the plan — the loan alone won&apos;t fix it.</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
